import {callAPI} from '@/store/utils';
import {DEMO_TRANSFER_ORDERS_BY_STATE} from '@/data/demoData';
const FYGR_COMPANY = 'FYGR_COMPANY';
const BENEFICIARY_TYPE_KEY = 'bankingOnboardingBeneficiaryType';

function loadBeneficiaryType() {
  try {
    const v = localStorage.getItem(BENEFICIARY_TYPE_KEY);
    return v === 'other' || v === 'owner' ? v : null;
  } catch {
    return null;
  }
}

export default {
  namespaced: true,
  state: {
    pending3ds: null, // array of obj.
    transferBatchOrders: null, // array of obj. — payments page tabs
    // Sales demo only: batches the rep "signs" during a demo, kept in memory so
    // they show up in the "En cours" tab and vanish on page refresh (no persist).
    demoInProgressBatches: [],
    pendingValidationTransferOrders: null,
    // Lot pain.001 analysé depuis une autre page (répertoire de contreparties) :
    // la signature 3SKey vit sur la page Virements, qui le consomme au montage.
    pendingBatchToSign: null,
    partnerApplication: null,
    bankingPartner: null,
    UBOs: null,
    administrativePeople: null,
    selectedBeneficiaryType: loadBeneficiaryType(),
    paymentModuleConditionsAccepted: false,
    // Transient: true right after the application is submitted so the
    // verification screen opens on the "Évaluation en cours" loader instead of
    // resolving immediately. Reset once the gate resolves.
    evaluating: false,
  },
  getters: {
    isPaymentApplicationActiveAndValidated: (state) => {
      const application = state.partnerApplication;
      if (!application) return false;
      const statusEquals = String(
        application?.status?.equals || '',
      ).toLowerCase();
      return (
        statusEquals === 'active' ||
        application?.investor_validated_by_fygr === true
      );
    },
    // At least one administrative person has received an identity-verification
    // email. Drives the "To verify" profile (recipients shown) vs "Verified".
    hasIdentityEmailsSent: (state) =>
      Boolean(
        Array.isArray(state.administrativePeople) &&
          state.administrativePeople.some(
            (p) => p?.identity_verification_email_sent === true,
          ),
      ),
    // Administrative people to whom an identity-verification email was sent.
    // NOTE: recipient shape pending confirmation with Harshad — keep this the
    // single source so the mapping is a one-line change later.
    identityRecipients: (state) => {
      const list = Array.isArray(state.administrativePeople)
        ? state.administrativePeople
        : [];
      const mapped = list
        .filter((p) => p?.identity_verification_email_sent === true)
        .map((p) => ({
          id: p?.id ?? null,
          name: [p?.first_name, p?.last_name].filter(Boolean).join(' ').trim(),
          email: p?.email || '',
          isApplicant: Boolean(p?.is_applicant) || p?.role === 'applicant',
        }));
      // The same email can appear several times (e.g. the applicant is also a
      // beneficiary). Keep a single entry per email, preferring the applicant
      // one. People without an email are never collapsed together.
      const byEmail = new Map();
      mapped.forEach((r, i) => {
        const key = r.email.trim().toLowerCase() || `__no-email-${r.id ?? i}`;
        const existing = byEmail.get(key);
        if (!existing || (r.isApplicant && !existing.isApplicant)) {
          byEmail.set(key, r);
        }
      });
      return Array.from(byEmail.values());
    },
  },
  mutations: {
    setPartner(state, data) {
      state.bankingPartner = data;
    },
    setPending3ds(state, data) {
      state.pending3ds = data;
    },
    setTransferBatchOrders(state, data) {
      state.transferBatchOrders = data;
    },
    addDemoInProgressBatch(state, batch) {
      // newest first, so a freshly signed demo batch shows on top of "En cours"
      state.demoInProgressBatches = [batch, ...state.demoInProgressBatches];
    },
    setPendingValidationTransferOrders(state, data) {
      state.pendingValidationTransferOrders = data;
    },
    setPendingBatchToSign(state, data) {
      state.pendingBatchToSign = data;
    },
    setPartnerApplication(state, data) {
      state.partnerApplication = data;
    },
    setSelectedBeneficiaryType(state, type) {
      state.selectedBeneficiaryType = type;
      try {
        if (type) {
          localStorage.setItem(BENEFICIARY_TYPE_KEY, type);
        } else {
          localStorage.removeItem(BENEFICIARY_TYPE_KEY);
        }
      } catch {
        /* ignore */
      }
    },
    setUBOs(state, data) {
      state.UBOs = data;
    },
    setAdministrativePeople(state, data) {
      state.administrativePeople = data;
    },
    setPaymentModuleConditionsAccepted(state, value) {
      state.paymentModuleConditionsAccepted = Boolean(value);
    },
    setEvaluating(state, value) {
      state.evaluating = Boolean(value);
    },
  },
  actions: {
    async actionSaveConditionsAccepted({commit}, payload = {}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', `api/accept-user-consents`, {
        company_id,
        ...payload,
      });
      if (res && !res.error) {
        commit('setPaymentModuleConditionsAccepted', true);
      }
      return res;
    },
    async actionGetConditionsAccepted({commit}, payload = {}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', `api/user-consents`, {
        company_id,
        ...payload,
      });
      if (res && !res.error) {
        commit(
          'setPaymentModuleConditionsAccepted',
          Boolean(res?.result?.accepted),
        );
      }
      return res;
    },

    async actionEnsurePartner(
      {state, commit, dispatch},
      partnerName = 'Equals',
    ) {
      const currentPartner = state.bankingPartner;
      if (currentPartner && currentPartner.id) {
        return currentPartner;
      }

      const res = await dispatch(
        'partner/actionGetPartners',
        {
          integration_flow: false,
          integration_for_regular: false,
        },
        {root: true},
      );

      const partner = res?.result?.find((i) => i.name === partnerName);
      if (partner) {
        commit('setPartner', partner);
        return partner;
      }

      await dispatch(
        'app/actionAddNotification',
        {
          msg: 'Erreur lors de la récupération du partenaire',
          color: 'red',
          disappearing: true,
          once: false,
        },
        {root: true},
      );
      return null;
    },
    async actionGetRegisteredUBOs({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${companyId}/registered-beneficiary-peoples`,
      );
      if (res?.result) {
        commit('setUBOs', res?.result);
      }
      return res;
    },
    async actionGetPending3DsValidation({commit}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${company_id}/equals/3ds-validation`,
      );
      if (res?.result) {
        commit('setPending3ds', res.result);
      }
      return res;
    },
    // Fetch a single pending 3DS challenge by its acs_transaction_id. The
    // company_id in the path is only for routing — the backend scopes the
    // lookup to the authenticated user and re-derives the challenge's own
    // company, so the currently-selected company is fine here.
    async actionGet3DsById(_ctx, acs_transaction_id) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${company_id}/equals/3ds-validation/${acs_transaction_id}`,
      );
      return res;
    },
    async actionGetTransferBatchOrders(ctx, {state} = {}) {
      const {commit, rootGetters} = ctx;
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const demo = Boolean(rootGetters['user/isDemoAccount']);
      const fixtures = demo ? (DEMO_TRANSFER_ORDERS_BY_STATE[state] ?? []) : [];
      const live =
        demo && state === 'in_progress'
          ? (ctx.state?.demoInProgressBatches ?? [])
          : [];

      let res;
      try {
        res = await callAPI(
          'GET',
          `company/${company_id}/transferBatchOrders?state=${state}`,
        );
      } catch (err) {
        if (!demo) throw err;
        console.warn('[demo] transferBatchOrders failed, fixtures only', err);
        commit('setTransferBatchOrders', [...live, ...fixtures]);
        return {error: true, result: [...live, ...fixtures]};
      }

      if (demo) {
        const real = !res?.error && Array.isArray(res?.result) ? res.result : [];
        commit('setTransferBatchOrders', [...live, ...real, ...fixtures]);
      } else if (res?.result) {
        commit('setTransferBatchOrders', res.result);
      }
      return res;
    },
    async actionArchiveTransferBatchOrders(_ctx, {batch_ids}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      return callAPI(
        'POST',
        `company/${company_id}/transferBatchOrders/archive`,
        {batch_ids},
      );
    },
    async actionGetTransferBatchDetail(_ctx, {batch_id}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      return callAPI(
        'GET',
        `company/${company_id}/transferBatchOrder/${batch_id}`,
      );
    },
    // Cancel a scheduled (not-yet-executed) payment: the backend flips the
    // order to `cancelled` — it leaves the lists — cancels its batch and
    // reverts the linked invoice + ETS from `pending_payment` to `ongoing`.
    // Nothing was sent to Equals yet, so there is no provider call. No mutation
    // here: callers refresh the list they are showing.
    async actionCancelScheduledTransfer(_ctx, {transfer_order_id}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      return callAPI('POST', 'equals/transfer/scheduled/cancel', {
        company_id,
        transfer_order_id,
      });
    },
    async actionGetPendingValidationTransferOrders({commit}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${company_id}/transferOrders/pending-validation`,
      );
      if (res?.result) {
        commit('setPendingValidationTransferOrders', res.result);
      }
      return res;
    },
    async actionDelPending3DsValidation({dispatch}, params) {
      // TT-6787: target the operation's own company (multi-company validators),
      // falling back to the selected company.
      const company_id =
        params.company_id || localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'DELETE',
        `company/${company_id}/equals/3ds-validation/${params.acs_transaction_id}`,
      );
      if (res?.result) {
        dispatch('actionGetPending3DsValidation');
      }
      return res;
    },
    async actionCreateAssociatedPeople(_ctx, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', `equals/create-associated-people`, {
        ...params,
        company_id,
      });

      return res;
    },
    async actionCreateBusinessApplication({state}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const partner_id = state.bankingPartner?.id ?? null;
      const res = await callAPI('POST', `equals/create-business-application`, {
        company_id,
        partner_id,
      });
      return res;
    },
    async actionSubmitIdentitiesOfPeople({state}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const partner_id = state.bankingPartner?.id ?? null;
      const res = await callAPI('POST', `equals/submit-identities-of-people`, {
        company_id,
        partner_id,
      });
      return res;
    },
    async actionPushApplicationDocuments({state}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const partner_id = state.bankingPartner?.id ?? null;
      const res = await callAPI(
        'POST',
        `equals/pushApplicationDocuments`,
        {company_id, partner_id},
        false,
        0,
      );
      return res;
    },
    async actionGetRequiredInformation({state}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const partner_id = state.bankingPartner?.id ?? null;
      if (!company_id || !partner_id) {
        return {result: {requiredInformation: [], associatedPeople: []}};
      }
      const res = await callAPI(
        'POST',
        'equals/get-additional-verification-info',
        {company_id, partner_id},
      );
      return res;
    },
    async actionPushAdditionalVerificationInformation(_ctx, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `equals/pushAdditionalVerificationInformation`,
        {...params, company_id},
      );
      return res;
    },
    async actionGetFinancialPartnerApplication({commit, state}) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const partner_id = state.bankingPartner?.id ?? null;
      if (!partner_id) {
        return;
      }
      const res = await callAPI(
        'GET',
        `company/${company_id}/partner/${partner_id}/financialPartnerApplication`,
      );
      if (res?.result) {
        commit('setPartnerApplication', res.result);
      }
      return res;
    },
    async actionGetAdministrativePeople({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${companyId}/partner/${params.partner}/administrativePeople`,
      );
      if (res?.result) {
        commit('setAdministrativePeople', res?.result);
      }
      return res;
    },
    async actionSaveAdministrativePeople(_ctx, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const partnerId = params.partnerId;
      const createPayload = params.createPayload ?? params.payload ?? [];
      const updatePayload = params.updatePayload ?? [];

      const postPath = `company/${companyId}/partner/${partnerId}/administrativePeople`;
      const requests = [];

      if (Array.isArray(createPayload) && createPayload.length > 0) {
        requests.push(callAPI('POST', postPath, createPayload));
      }

      // Update existing administrative people one by one with PUT
      if (Array.isArray(updatePayload) && updatePayload.length > 0) {
        updatePayload.forEach((person) => {
          if (!person?.id) return;
          const {id, ...body} = person;
          const putPath = `company/${companyId}/administrativePeople/${id}`;
          requests.push(callAPI('PUT', putPath, body));
        });
      }

      if (!requests.length) return null;

      const results = await Promise.all(requests);
      return results[results.length - 1] ?? null;
    },
    async actionDeleteAdministrativePeople(_ctx, id) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'DELETE',
        `company/${companyId}/administrativePeople/${id}`,
      );
      return res;
    },
    async actionSaveDocuments(_ctx, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', `equals/uploadApplicationDocument`, {
        ...params,
        company_id,
      });
      return res;
    },

    async actionDeleteDocument({state}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const partnerId = state.bankingPartner?.id ?? null;
      const financialPartnerApplicationId =
        state.partnerApplication?.id ?? null;

      const res = await callAPI('DELETE', 'deleteDocument', {
        ...params,
        companyId,
        partnerId,
        financialPartnerApplicationId,
      });
      return res;
    },
    async actionGetDocuments({state}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const partnerId = state.bankingPartner?.id ?? null;
      const financialPartnerApplicationId =
        state.partnerApplication?.id ?? null;

      const res = await callAPI('POST', 'getInvestorDocuments', {
        companyId,
        partnerId,
        financialPartnerApplicationId,
      });
      return res;
    },
    async actionSinglePrepare(_ctx, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'transfer/single/prepare', {
        company_id,
        ...params,
      });
      return res;
    },
    async actionSingleSubmit(_ctx, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'transfer/single/submit', {
        company_id,
        ...params,
      });
      return res;
    },
    async actionBatchFromInvoices(_ctx, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'transfer/batch/from-invoices', {
        company_id,
        ...params,
      });
      return res;
    },
    async actionBatchInit(_ctx, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'transfer/batch/init', {
        company_id,
        connector_type: 'ebics',
        ...params,
      });
      return res;
    },
    async actionBatchPrepare(_ctx, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      return await callAPI('POST', 'transfer/batch/prepare', {
        company_id,
        ...params,
      });
    },
    async actionParseTransferBatch(_ctx, formData) {
      formData.append('company_id', localStorage.getItem(FYGR_COMPANY));
      return await callAPI('POST', 'transfer/batch/parse', formData);
    },
    async actionBatchSubmit(_ctx, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'transfer/batch/submit', {
        company_id,
        ...params,
      });
      return res;
    },

    async actionSubmitTransferBatch(_ctx, params) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'transfer/batch/submit', {
        company_id,
        ...params,
      });
      return res;
    },
    async actionGetRib(_ctx, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `downloadBankAccountDetails`,
        {
          companyId,
          ...params,
        },
        false,
        20,
        0,
        true,
      );
      return res;
    },
    async actionGetStatements(_ctx, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `downloadBankAccountStatements`,
        {
          companyId,
          ...params,
        },
        false,
        20,
        0,
        true,
      );
      return res;
    },
    async actionGetAvailableCurrencies(_ctx) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${company_id}/equals/currencies`,
      );
      return res;
    },
    async actionEditCurrencies(_ctx, payload) {
      const company_id = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'PATCH',
        `company/${company_id}/equals/currencies`,
        payload,
      );
      return res;
    },
  },
};
